import { DefaultRecordingTemplates } from "@/common/enums/enums";
import type { Template } from "@/common/types/templates";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import { isBuiltInTemplate } from "./template-preferences";

// The note settings a v3.2 recording is submitted with when Stop goes straight
// to the note (VITE_RECORDER_V32_ENABLED): the template chosen on the
// recorder's chip, or else the EMR launch template, or else the account's
// default (lib/template-preferences). Style and format come from the
// clinician's settings, as on the classic submit sheet; the examination is
// "Physical" unless the chip says "Virtual".

export type Examination = "Physical" | "Virtual";
export const EXAMINATIONS: readonly Examination[] = ["Physical", "Virtual"];

// Built-in templates other than Voice to Text Dictation take style, format and
// examination; custom templates and dictation do not.
export const hasNoteOptions = (template: string): boolean =>
  isBuiltInTemplate(template) &&
  template !== DefaultRecordingTemplates.VOICE_TO_TEXT.toString();

const exists = (
  template: string | undefined,
  templates: readonly Pick<Template, "public_id">[],
): template is string =>
  !!template &&
  (isBuiltInTemplate(template) ||
    templates.some((item) => item.public_id === template));

// The template the recording uses. A choice or launch template that is not
// (or no longer) available falls back to the default.
export const pickRecorderTemplate = ({
  chosen,
  preferred,
  fallback,
  templates,
}: {
  chosen?: string | null;
  preferred?: string | null;
  fallback: string;
  templates: readonly Pick<Template, "public_id">[];
}): string => {
  if (exists(chosen ?? undefined, templates)) return chosen as string;
  if (exists(preferred ?? undefined, templates)) return preferred as string;
  return fallback;
};

// The same values the classic submit sheet sends (recorder-form onSubmit).
// Null when there is no usable template (an old numeric default that the
// clinician has to pick again): the sheet's form is shown instead.
export const toRecorderFormData = ({
  template,
  examination = "Physical",
  templates,
  style,
  format,
}: {
  template: string;
  examination?: Examination;
  templates: readonly Pick<Template, "public_id">[];
  style?: string | null;
  format?: string | null;
}): RecorderFormSchemaType | null => {
  if (!exists(template, templates)) return null;
  if (!isBuiltInTemplate(template))
    return {
      template,
      templateName: "Custom",
      customTemplatePublicId: template,
      style: "",
      format: "",
      examination: "",
    };
  const options = hasNoteOptions(template);
  return {
    template,
    templateName: template,
    customTemplatePublicId: null,
    style: options ? style || "Bullet Point" : "",
    format: options ? format || "Concise" : "",
    examination: options ? examination : "",
  };
};
